/**
 * Batches tab on the project detail page.
 *
 * Lists every part (variant B) and sub-part (variant C) that can carry order
 * batches, with their batches shown inline. Each batch row shows supplier,
 * quantity, current procurement stage (1-5), and lets the user advance dates
 * inline via a small calendar input.
 *
 * Variant A parts (with sub-parts) are not directly batch-carriers; their
 * sub-parts are. This view filters them out.
 *
 * Gates respected client-side:
 *   - Batch create button only enabled when parts workflow is FINALISED
 *   - Stage date inputs respect the sequence (each next stage only enables
 *     once the previous date is set)
 *   - Cancel + Replace actions are Admin-only on the server; the buttons
 *     check current user's role too
 */
import { useState } from 'react';
import { Ban, Package, Plus, Repeat } from 'lucide-react';
import { formatDate, todayPktIsoDate } from '@procurement/shared';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DmyDateInput } from '@/components/forms/dmy-date-input';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ExportCsvButton } from '@/components/ui/export-csv-button';
import { FormField, Input } from '@/components/ui/form-field';
import { Modal } from '@/components/ui/modal';
import { ReplaceBatchModal } from '@/components/replace-batch-modal';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { SortableHeader } from '@/components/ui/sortable-header';
import { Spinner } from '@/components/ui/spinner';
import { StatusBadge } from '@/components/ui/status-badge';
import { useCurrentUser } from '@/lib/auth';
import {
  useBatches,
  useCancelBatch,
  useCreateBatch,
  useCreateBulkBatches,
  useProjectItems,
  useSuppliersList,
  useUpdateBatch,
  type OrderBatch,
  type ProjectItem,
} from '@/lib/pipeline-queries';
import type { Project } from '@/lib/project-queries';
import { isLockedForUser } from '@/lib/closure-lock';

interface Props {
  project: Project;
}

export function ProjectBatchesTab({ project }: Props) {
  const items = useProjectItems(project.id);
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  // Patch 5 — sortable carrier list. Client-side sort. Default srNumber asc.
  type BatchCarrierSortKey = 'srNumber' | 'name' | 'currentStageNumber';
  const [sortBy, setSortBy] = useState<BatchCarrierSortKey>('srNumber');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [showBulkCreate, setShowBulkCreate] = useState(false);
  const toggleSort = (key: BatchCarrierSortKey): void => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'currentStageNumber' ? 'desc' : 'asc');
    }
  };

  if (items.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }

  // Batch-carriers: variant B (PART with no sub-parts) + variant C (SUB_PART)
  const carriersUnsorted = (items.data ?? []).filter(
    (i) => (i.itemType === 'PART' && !i.hasSubParts) || i.itemType === 'SUB_PART',
  );
  const carriers = [...carriersUnsorted].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name) * dir;
      case 'currentStageNumber':
        return (a.currentStageNumber - b.currentStageNumber) * dir;
      case 'srNumber':
      default: {
        const aN = a.srNumber ?? Number.POSITIVE_INFINITY;
        const bN = b.srNumber ?? Number.POSITIVE_INFINITY;
        return (aN - bN) * dir;
      }
    }
  });

  // Spec §13 — "Log order batches | ✓ | ✓"; closure locks Assistants only.
  const locked = isLockedForUser(project, isAdmin);
  const canOrder = project.partsWorkflowState === 'FINALISED' && !locked;
  const bulkCandidates = carriers.filter((item) => item.totalRequiredQuantity - item.activeOrderedQuantity > 0);

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-secondary">
          {!canOrder ? (
            <span>
              Batches can be placed once the parts list is FINALISED
              {locked ? ' (project is closed)' : ''}.
            </span>
          ) : (
            <span>{carriers.length} batch-carrying parts</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canOrder && bulkCandidates.length > 0 ? (
            <Button
              variant="secondary"
              size="compact"
              leftIcon={<Plus className="h-3.5 w-3.5" />}
              onClick={() => setShowBulkCreate(true)}
            >
              Bulk create
            </Button>
          ) : null}
          {carriers.length > 0 ? (
            <ExportCsvButton
              href={`/api/exports/projects/${project.id}/batches`}
              fallbackFilename="batches.csv"
            />
          ) : null}
        </div>
      </div>

      {carriers.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Package className="h-6 w-6" />}
            title={
              project.partsWorkflowState === 'FINALISED'
                ? 'No batch-carrying parts'
                : 'Parts not yet finalised'
            }
            body={
              project.partsWorkflowState === 'FINALISED'
                ? 'Every part on this project is a variant A (composed of sub-parts). Sub-parts carry the batches and appear here.'
                : 'Finalise the parts list to start placing order batches.'
            }
          />
        </Card>
      ) : (
        <>
          {/* Patch 5 — sort bar. Same pattern as the parts tab. */}
          <div className="mb-3 flex items-center gap-3 rounded-md border border-border-default bg-base px-3 py-1.5 text-xs text-secondary">
            <span className="font-semibold uppercase tracking-wide">Sort</span>
            <SortableHeader
              label="Sr #"
              active={sortBy === 'srNumber'}
              direction={sortDir}
              onToggle={() => toggleSort('srNumber')}
            />
            <SortableHeader
              label="Name"
              active={sortBy === 'name'}
              direction={sortDir}
              onToggle={() => toggleSort('name')}
            />
            <SortableHeader
              label="Stage"
              active={sortBy === 'currentStageNumber'}
              direction={sortDir}
              onToggle={() => toggleSort('currentStageNumber')}
            />
          </div>
          <div className="space-y-4">
            {carriers.map((item) => (
              <CarrierCard key={item.id} project={project} item={item} canOrder={canOrder} />
            ))}
          </div>
        </>
      )}

      {showBulkCreate ? (
        <BulkCreateBatchesModal
          project={project}
          items={bulkCandidates}
          onClose={() => setShowBulkCreate(false)}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// One card per batch-carrier (part-B or sub-part), batches listed inside
// ---------------------------------------------------------------------------

function CarrierCard({
  project,
  item,
  canOrder,
}: {
  project: Project;
  item: ProjectItem;
  canOrder: boolean;
}) {
  const batches = useBatches(project.id, item.id);
  const [showCreate, setShowCreate] = useState(false);

  const remainingQty = item.totalRequiredQuantity - item.activeOrderedQuantity;
  // Spec §7.5 — over-ordered surfaces here too. Soft, informational.
  const overOrderedBy = remainingQty < 0 ? -remainingQty : 0;

  return (
    <Card
      title={
        <div className="flex items-center gap-2">
          <span>{item.name}</span>
          {item.itemType === 'SUB_PART' ? <Badge tone="info">Sub-part</Badge> : null}
          {item.partialBatchIndicator ? (
            <Badge tone="warning">{item.partialBatchIndicator}</Badge>
          ) : null}
          {overOrderedBy > 0 ? (
            <Badge tone="warning">Over-ordered by {overOrderedBy}</Badge>
          ) : null}
        </div>
      }
      actions={
        <div className="flex items-center gap-3 text-xs text-secondary">
          <span>
            {item.activeOrderedQuantity}/{item.totalRequiredQuantity} ordered
          </span>
          {canOrder && remainingQty > 0 ? (
            <Button
              variant="secondary"
              size="compact"
              leftIcon={<Plus className="h-3.5 w-3.5" />}
              onClick={() => setShowCreate(true)}
            >
              New batch
            </Button>
          ) : null}
        </div>
      }
      bodyClassName="p-0"
    >
      {batches.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner className="h-5 w-5 text-secondary" />
        </div>
      ) : (batches.data?.length ?? 0) === 0 ? (
        <EmptyState
          title="No batches yet"
          body={
            canOrder
              ? `Place the first order batch — up to ${remainingQty} units remaining.`
              : 'Once parts are finalised, place the first order batch here.'
          }
        />
      ) : (
        <div className="divide-y divide-border-default">
          {(batches.data ?? []).map((batch) => (
            <BatchRow key={batch.id} project={project} item={item} batch={batch} />
          ))}
        </div>
      )}

      {showCreate ? (
        <CreateBatchModal
          project={project}
          item={item}
          remainingQty={remainingQty}
          onClose={() => setShowCreate(false)}
        />
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Single batch row with inline stage-date editing
// ---------------------------------------------------------------------------

function BatchRow({
  project,
  item,
  batch,
}: {
  project: Project;
  item: ProjectItem;
  batch: OrderBatch;
}) {
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  const update = useUpdateBatch(project.id, item.id);
  const cancel = useCancelBatch(project.id, item.id);
  const [showCancel, setShowCancel] = useState(false);
  const [showReplace, setShowReplace] = useState(false);

  // Sequence-aware stage dates: each next one only editable when the prior is set
  // Patch E14 — spec §7.5: Pre-shipment and Transit are OPTIONAL; their absence
  // does not gate later stages. Only stage 5 (In-house) legitimately requires
  // its predecessor (Arrived). See batch-surface.tsx for the matching fix.
  const stages = [
    {
      num: 1,
      label: 'Order placed',
      field: 'orderPlacedDate' as const,
      value: batch.orderPlacedDate,
      enabled: true,
    },
    {
      num: 2,
      label: 'Pre-shipment inspection',
      field: 'preShipmentInspectionDate' as const,
      value: batch.preShipmentInspectionDate,
      enabled: Boolean(batch.orderPlacedDate),
    },
    {
      num: 3,
      label: 'In transit',
      field: 'inTransitDate' as const,
      value: batch.inTransitDate,
      enabled: Boolean(batch.orderPlacedDate),
    },
    {
      num: 4,
      label: 'Arrived',
      field: 'arrivedDate' as const,
      value: batch.arrivedDate,
      enabled: Boolean(batch.orderPlacedDate),
    },
    {
      num: 5,
      label: 'In-house inspection',
      field: 'inHouseInspectionDate' as const,
      value: batch.inHouseInspectionDate,
      enabled: Boolean(batch.arrivedDate),
    },
  ];

  const isClosed = batch.status !== 'ACTIVE';

  const onUpdate = (field: keyof OrderBatch, value: string): void => {
    update.mutate({
      batchId: batch.id,
      rowVersion: batch.rowVersion,
      [field]: value || null,
    });
  };

  return (
    <div className="p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <StatusBadge kind="batch" value={batch.status} />
          <span className="text-sm font-medium text-primary">
            {batch.supplierName ?? batch.supplierNameSnapshot}
          </span>
          <span className="text-xs text-tertiary">·</span>
          <span className="text-sm text-secondary tabular-nums">{batch.quantity} units</span>
          {batch.isOverdue ? <Badge tone="danger">Overdue</Badge> : null}
        </div>
        <div className="flex items-center gap-2 text-xs text-secondary">
          <span>ETA {formatDate(batch.expectedArrivalDate)}</span>
          {batch.status === 'ACTIVE' && isAdmin ? (
            <>
              <Button
                variant="tertiary"
                size="compact"
                leftIcon={<Repeat className="h-3.5 w-3.5" />}
                onClick={() => setShowReplace(true)}
              >
                Replace
              </Button>
              <Button
                variant="tertiary"
                size="compact"
                leftIcon={<Ban className="h-3.5 w-3.5" />}
                onClick={() => setShowCancel(true)}
              >
                Cancel
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
        {stages.map((s) => (
          <div key={s.num}>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-tertiary">
              {s.num}. {s.label}
            </label>
            <DmyDateInput
              value={s.value ?? ''}
              disabled={isClosed || !s.enabled}
              onChange={(value) => onUpdate(s.field, value)}
              showValidationMessage={false}
              className="mt-1 h-8"
            />
          </div>
        ))}
      </div>

      {update.error ? (
        <Alert tone="danger" className="mt-3">
          {update.error.message}
        </Alert>
      ) : null}

      {showCancel ? (
        <Modal
          open
          onClose={() => setShowCancel(false)}
          title="Cancel this batch"
          description="The batch is marked CANCELLED. You can later place a replacement batch for the unfulfilled quantity."
          size="sm"
          footer={
            <>
              <Button variant="tertiary" onClick={() => setShowCancel(false)}>
                Keep
              </Button>
              <CancelConfirm
                onConfirm={(reason) => {
                  cancel.mutate(
                    { batchId: batch.id, cancellationReason: reason, rowVersion: batch.rowVersion },
                    { onSuccess: () => setShowCancel(false) },
                  );
                }}
                pending={cancel.isPending}
                error={cancel.error?.message ?? null}
              />
            </>
          }
        >
          <CancelInner />
        </Modal>
      ) : null}

      {showReplace ? (
        <ReplaceBatchModal
          open
          onClose={() => setShowReplace(false)}
          projectId={project.id}
          itemId={item.id}
          batch={batch}
        />
      ) : null}
    </div>
  );
}

// Helper to host the cancel-modal state inside its own component (closure over
// `reason` state) without dragging it up to BatchRow.
function CancelInner() {
  return (
    <p className="text-sm text-secondary">
      Enter a brief reason. This is captured in the audit log.
    </p>
  );
}

function CancelConfirm({
  onConfirm,
  pending,
  error,
}: {
  onConfirm: (reason: string) => void;
  pending: boolean;
  error: string | null;
}) {
  const [reason, setReason] = useState('');
  return (
    <>
      <div className="flex w-full flex-col gap-2">
        {error ? (
          <Alert tone="danger" className="text-xs">
            {error}
          </Alert>
        ) : null}
        <Input
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason"
        />
        <div className="flex justify-end">
          <Button
            variant="destructive"
            onClick={() => onConfirm(reason.trim() || 'Cancelled')}
            loading={pending}
          >
            Confirm cancel
          </Button>
        </div>
      </div>
    </>
  );
}


function BulkCreateBatchesModal({
  project,
  items,
  onClose,
}: {
  project: Project;
  items: ProjectItem[];
  onClose: () => void;
}) {
  const createBulk = useCreateBulkBatches(project.id);
  const suppliers = useSuppliersList();
  const [supplierId, setSupplierId] = useState('');
  const [orderPlaced, setOrderPlaced] = useState(todayPktIsoDate());
  const [eta, setEta] = useState('');
  const [rows, setRows] = useState<Record<string, { selected: boolean; quantity: string }>>(() =>
    Object.fromEntries(
      items.map((item) => [
        item.id,
        {
          selected: item.totalRequiredQuantity - item.activeOrderedQuantity > 0,
          quantity: String(Math.max(0, item.totalRequiredQuantity - item.activeOrderedQuantity)),
        },
      ]),
    ),
  );

  const selected = items
    .map((item) => {
      const state = rows[item.id];
      const quantity = Number(state?.quantity ?? 0);
      return { item, state, quantity };
    })
    .filter((row) => row.state?.selected && Number.isInteger(row.quantity) && row.quantity > 0);
  const required = supplierId && orderPlaced && eta && selected.length > 0;

  const submit = (): void => {
    if (!required) return;
    createBulk.mutate(
      {
        batches: selected.map(({ item, quantity }) => ({
          projectItemId: item.id,
          supplierId,
          quantity,
          orderPlacedDate: orderPlaced,
          expectedArrivalDate: eta,
        })),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Bulk create batches"
      description="Select multiple parts/sub-parts. Suggested quantities are the remaining required quantities."
      size="lg"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!required}
            loading={createBulk.isPending}
          >
            Create {selected.length || ''} batch{selected.length === 1 ? '' : 'es'}
          </Button>
        </>
      }
    >
      {createBulk.error ? (
        <Alert tone="danger" className="mb-3">
          {createBulk.error.message}
        </Alert>
      ) : null}

      <div className="grid gap-3 md:grid-cols-3">
        <FormField label="Supplier" required>
          <SearchableSelect
            items={(suppliers.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
            value={supplierId || null}
            onChange={(v) => setSupplierId(v ?? '')}
            placeholder="Select a supplier…"
          />
        </FormField>
        <FormField label="Order placed date" required>
          <DmyDateInput value={orderPlaced} onChange={setOrderPlaced} />
        </FormField>
        <FormField label="Expected arrival date" required>
          <DmyDateInput value={eta} onChange={setEta} />
        </FormField>
      </div>

      <div className="mt-4 max-h-[420px] overflow-auto rounded-lg border border-border-default">
        <table className="min-w-full divide-y divide-border-default text-sm">
          <thead className="bg-subtle text-xs uppercase tracking-wide text-tertiary">
            <tr>
              <th className="px-3 py-2 text-left">Use</th>
              <th className="px-3 py-2 text-left">Part / sub-part</th>
              <th className="px-3 py-2 text-right">Required</th>
              <th className="px-3 py-2 text-right">Ordered</th>
              <th className="px-3 py-2 text-right">Suggested qty</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-default">
            {items.map((item) => {
              const remaining = item.totalRequiredQuantity - item.activeOrderedQuantity;
              const state = rows[item.id] ?? { selected: false, quantity: '0' };
              return (
                <tr key={item.id}>
                  <td className="px-3 py-2 align-middle">
                    <input
                      type="checkbox"
                      checked={state.selected}
                      onChange={(e) =>
                        setRows((prev) => ({
                          ...prev,
                          [item.id]: { ...state, selected: e.target.checked },
                        }))
                      }
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-primary">{item.name}</div>
                    <div className="text-xs text-secondary">
                      {item.itemType === 'SUB_PART' ? 'Sub-part' : 'Part'} · Sr #{item.srNumber ?? '—'}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{item.totalRequiredQuantity}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{item.activeOrderedQuantity}</td>
                  <td className="px-3 py-2 text-right">
                    <Input
                      type="number"
                      min={1}
                      max={Math.max(remaining, 1)}
                      value={state.quantity}
                      onChange={(e) =>
                        setRows((prev) => ({
                          ...prev,
                          [item.id]: { ...state, quantity: e.target.value },
                        }))
                      }
                      className="ml-auto h-8 w-24 text-right"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Create batch modal
// ---------------------------------------------------------------------------

function CreateBatchModal({
  project,
  item,
  remainingQty,
  onClose,
}: {
  project: Project;
  item: ProjectItem;
  remainingQty: number;
  onClose: () => void;
}) {
  const create = useCreateBatch(project.id, item.id);
  const suppliers = useSuppliersList();
  const [supplierId, setSupplierId] = useState('');
  const [quantity, setQuantity] = useState(String(remainingQty));
  const [orderPlaced, setOrderPlaced] = useState(todayPktIsoDate());
  const [eta, setEta] = useState('');

  const required = supplierId && Number(quantity) > 0 && orderPlaced && eta;
  const submit = (): void => {
    if (!required) return;
    create.mutate(
      {
        supplierId,
        quantity: Number(quantity),
        orderPlacedDate: orderPlaced,
        expectedArrivalDate: eta,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`New batch · ${item.name}`}
      description={`Up to ${remainingQty} units remaining.`}
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!required}
            loading={create.isPending}
          >
            Create batch
          </Button>
        </>
      }
    >
      {create.error ? (
        <Alert tone="danger" className="mb-3">
          {create.error.message}
        </Alert>
      ) : null}
      <FormField label="Supplier" required>
        <SearchableSelect
          items={(suppliers.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
          value={supplierId || null}
          onChange={(v) => setSupplierId(v ?? '')}
          placeholder="Select a supplier…"
        />
      </FormField>
      <FormField label="Quantity" required>
        <Input
          type="number"
          min={1}
          max={remainingQty}
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
        />
      </FormField>
      <FormField label="Order placed date" required>
        <DmyDateInput value={orderPlaced} onChange={setOrderPlaced} />
      </FormField>
      <FormField label="Expected arrival date" required>
        <DmyDateInput value={eta} onChange={setEta} />
      </FormField>
    </Modal>
  );
}
